/* Blend event planner: Shapes 4.3.1 / native Shopify contact form. */
class BlendEventPlanner extends HTMLElement {
  async connectedCallback() {
    if (this.initialized || this.initializing) return;
    this.form = this.querySelector('form');
    if (!this.form || this.querySelector('[data-server-success]')) return;
    const lifecycle = new AbortController();
    this.lifecycle = lifecycle;
    this.initializing = true;
    try {
      const core = await import(this.dataset.coreUrl);
      if (lifecycle.signal.aborted || !this.isConnected) return;
      this.core = core;
      this.config = core.normalizeConfig(JSON.parse(this.dataset.config));
    } catch {
      // Restore Shopify's complete native form if the core cannot load.
      if (!lifecycle.signal.aborted) {
        this.querySelectorAll('[data-step], [data-submit]').forEach(element => { element.hidden = false; });
      }
      return;
    } finally {
      if (this.lifecycle === lifecycle) this.initializing = false;
    }
    const options = { signal: lifecycle.signal };
    this.steps = [...this.querySelectorAll('[data-step]')];
    this.form.noValidate = true;
    this.field('date').min = this.core.todayInFinland();
    this.querySelector('.bep-progress').hidden = false;
    this.querySelector('[data-next]').addEventListener('click', () => this.next(), options);
    this.querySelector('[data-back]').addEventListener('click', () => this.showStep(this.step - 1), options);
    // Validate before emitting a submit event, so CAPTCHA only sees valid requests.
    this.querySelector('[data-submit]').type = 'button';
    this.querySelector('[data-submit]').addEventListener('click', () => this.requestSubmission(), options);
    const edited = event => {
      event.target.removeAttribute('aria-invalid');
      this.update();
    };
    this.form.addEventListener('change', edited, options);
    this.form.addEventListener('input', edited, options);
    this.form.addEventListener('keydown', event => {
      if (event.key === 'Enter' && !event.isComposing && event.target instanceof HTMLInputElement) {
        event.preventDefault();
        if (this.step < 3) this.next();
        else this.requestSubmission();
      }
    }, options);
    this.form.addEventListener('submit', event => this.submit(event), options);
    this.initialized = true;
    this.showStep(this.querySelector('[data-server-errors]') ? 3 : (this.step ?? 0), false);
    this.update();
    this.setAttribute('data-enhanced', 'true');
  }
  disconnectedCallback() {
    this.lifecycle?.abort();
    this.initializing = false;
    this.initialized = false;
    this.removeAttribute('data-enhanced');
    if (this.form) this.form.noValidate = false;
    const submitButton = this.querySelector('[data-submit]');
    if (submitButton) submitButton.type = 'submit';
  }
  field(key) { return this.querySelector(`[data-field="${key}"]`); }
  read() {
    const value = key => { const first = this.field(key); return first?.type === 'radio' ? this.querySelector(`[data-field="${key}"]:checked`)?.value || '' : first?.value.trim() || ''; };
    return Object.fromEntries(['event','guests','date','city','service','preference','drinksPerGuest','hours','name','email','phone','notes','website'].map(key => [key, value(key)]));
  }
  error(message, preventScroll = false) {
    const box = this.querySelector('[data-error]'); box.textContent = message; box.hidden = !message;
    if (message) box.focus({ preventScroll });
  }
  showStep(index, focus = true) {
    this.step = Math.min(3, Math.max(0, index));
    this.steps.forEach((panel, i) => { panel.hidden = i !== this.step; });
    this.querySelectorAll('.bep-progress li').forEach((item, i) => {
      item.classList.toggle('is-done', i < this.step);
      if (i === this.step) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current');
    });
    this.querySelector('[data-back]').hidden = this.step === 0;
    this.querySelector('[data-next]').hidden = this.step === 3;
    this.querySelector('[data-submit]').hidden = this.step !== 3;
    this.error('');
    if (focus) {
      const legend = this.steps[this.step].querySelector('legend');
      legend.focus({ preventScroll: true });
    }
    if (this.step === 3) this.update();
  }
  validate(currentOnly = false) {
    const errors = this.core.validateRequest(this.read());
    const keysByStep = [['event'], ['guests','date','city'], ['service','preference','hours','drinksPerGuest'], ['name','email','phone','notes']];
    const first = Object.keys(errors).find(key => !currentOnly || keysByStep[this.step].includes(key));
    if (!first) return true;
    const targetStep = keysByStep.findIndex(keys => keys.includes(first));
    if (this.step !== targetStep) this.showStep(targetStep);
    this.error(errors[first], currentOnly);
    this.querySelectorAll(`[data-field="${first}"]`).forEach(field => field.setAttribute('aria-invalid', 'true'));
    this.field(first)?.focus({ preventScroll: currentOnly });
    return false;
  }
  next() {
    const { scrollX: left, scrollY: top } = window;
    if (this.validate(true)) this.showStep(this.step + 1);
    // Keep the viewport fixed when the browser anchors a differently sized step.
    window.scrollTo({ left, top, behavior: 'instant' });
  }
  update() {
    const data = this.read();
    this.querySelector('[data-hours-wrap]').hidden = data.service !== 'bar';
    this.field('hours').disabled = data.service !== 'bar';
    const quote = this.core.calculateQuote(data, this.config);
    this.querySelector('[data-summary]').hidden = false;
    this.querySelector('[data-package]').textContent = quote.packageName;
    this.querySelector('[data-description]').textContent = quote.description;
    this.querySelector('[data-quantity]').textContent = quote.quantity ? `Alustava juomamäärä: ${quote.quantity} kpl` : 'Sovitaan juomamäärä yhdessä.';
    const price = this.querySelector('[data-price]');
    price.hidden = quote.totalCents === null;
    price.textContent = price.hidden ? '' : this.core.money(quote.totalCents);
    this.querySelector('[data-tax]').textContent = quote.totalCents === null ? '' : `Alustava arvio ilman kuljetusta. ${quote.taxLabel}.`;
    this.querySelector('[data-note]').textContent = quote.note;
    const rows = quote.lines.map(line => { const li = document.createElement('li'); const label = document.createElement('span'); const amount = document.createElement('span'); label.textContent = line.label; amount.textContent = this.core.money(line.cents); li.append(label, amount); return li; });
    this.querySelector('[data-lines]').replaceChildren(...rows);
    this.querySelector('[data-body]').value = this.core.requestSummary(data, quote);
  }
  requestSubmission() {
    if (this.step < 3) { this.next(); return; }
    if (!this.validate() || this.read().website) return;
    this.update();
    this.form.requestSubmit();
  }
  submit(event) {
    if (this.step < 3) { event.preventDefault(); this.next(); return; }
    if (!this.validate() || this.read().website) { event.preventDefault(); return; }
    this.update();
    // Keep the native contact submission and Shopify's CAPTCHA intact.
    // requestSubmit() retains submit events and Shopify's CAPTCHA integration.
  }
}
if (!customElements.get('blend-event-planner')) customElements.define('blend-event-planner', BlendEventPlanner);
